import React from 'react';

import { isDefined } from '../../../../utils/variables';
import { ElementObj } from '../../../../viewer/elements/element-obj';
import { SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useSubscribe } from '../../../hooks/use-subscribe';

type VisibilitySnapshot = {
  groupVisible: boolean;
  visibleAssetUuids: Set<string>;
};

/**
 * Lets you toggle visibility of a group of assets. When visibility is set to hidden, all current
 * visible assets are preserved to later be displayed when group visibility is reset to visible.
 */
export const useAssetGroupVisibility = (assets: ElementObj[], defaultVisibility: boolean) => {
  const [visible, setVisible] = React.useState<boolean>(defaultVisibility);
  const [assetsVisible, setAssetsVisible] = React.useState<ElementObj[]>([]);
  const [visibilitySnapshot, setVisibilitySnapshot] = React.useState<
    VisibilitySnapshot | undefined
  >(undefined);

  const toggleVisibility = React.useCallback(async () => {
    const hideVisibleAssets = async () => {
      const visibleAssets = assets.filter((x) => x.visible);

      setAssetsVisible(visibleAssets);
      for (const asset of visibleAssets) {
        await asset.toggleVisibility(false);
      }
    };

    const showHiddenAssets = async () => {
      for (const asset of assetsVisible) {
        await asset.toggleVisibility(true);
      }
    };

    setVisible(!visible);

    if (visible) {
      await hideVisibleAssets();
    } else {
      await showHiddenAssets();
    }
  }, [assets, assetsVisible, visible]);

  // Take a visibility snapshot of asset group and all assets.
  useSubscribe(SubscriptionTopic.SaveAssetMenuVisibilitySnapshot, () => {
    setVisibilitySnapshot({
      groupVisible: visible,
      visibleAssetUuids: new Set(assets.filter((x) => x.visible).map((x) => x.uuid)),
    });
  });

  // Restore visibility of asset group and all assets.
  useSubscribe(SubscriptionTopic.RestoreAssetMenuVisibilitySnapshot, async () => {
    if (isDefined(visibilitySnapshot)) {
      setVisible(visibilitySnapshot.groupVisible);
      for (const asset of assets) {
        await asset.toggleVisibility(visibilitySnapshot.visibleAssetUuids.has(asset.uuid));
      }

      setVisibilitySnapshot(undefined);
    }
  });

  useSubscribe(SubscriptionTopic.ShowOnlySpecificAssets, (message) => {
    // Show group if at least one asset in group is visible.
    if (assets.some((x) => message.assetUuids.includes(x.uuid))) {
      setVisible(true);
    }
  });

  return {
    visible,
    toggleVisibility,
  };
};
